"use client";

import { c as _c } from "react/compiler-runtime";
import { IconArrowRotateClockwiseOutline18, IconCheckOutline18, IconCopyOutline18, IconEyeOutline18, IconEyeSlashOutline18, IconLink2Outline18, IconTerminalOutline18 } from "nucleo-ui-outline-18";
import { Button } from "@/components/ui/button";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { generatePerWorkflowCurlCommand, generatePerWorkflowWebhookUrl } from "@/utils/webhookUtils";
import { useState } from "react";
interface WebhookUrlSectionProps {
  workflowId: string;
  /** The workflow's per-workflow webhook secret */
  secret: string;
  /** Optional input schema to derive an example body for the curl command */
  inputSchema?: Record<string, unknown>;
  /** Callback to regenerate the secret */
  onRegenerateSecret?: () => void;
  /** Whether a secret regeneration is in progress */
  isRegenerating?: boolean;
  className?: string;
}

/**
 * Derive an example body from a JSON schema-like inputSchema.
 * Produces placeholder values for each property.
 */
function deriveExampleBody(schema: Record<string, unknown>): Record<string, unknown> {
  const properties = schema.properties as Record<string, Record<string, unknown>> | undefined;
  if (!properties) return {
    key: "value"
  };
  const example: Record<string, unknown> = {};
  for (const [key, prop] of Object.entries(properties)) {
    const type = prop.type as string | undefined;
    switch (type) {
      case "number":
      case "integer":
        example[key] = 0;
        break;
      case "boolean":
        example[key] = true;
        break;
      case "array":
        example[key] = [];
        break;
      case "object":
        example[key] = {};
        break;
      default:
        example[key] = `your_${key}`;
    }
  }
  return Object.keys(example).length > 0 ? example : {
    key: "value"
  };
}

/**
 * Collapsible section displaying a per-workflow webhook URL and curl command
 * with copy functionality and optional secret regeneration.
 */
export function WebhookUrlSection(t0) {
  const $ = _c(30);
  const {
    workflowId,
    secret,
    inputSchema,
    onRegenerateSecret,
    isRegenerating: t1,
    className: t2
  } = t0;
  const isRegenerating = t1 === undefined ? false : t1;
  const className = t2 === undefined ? "" : t2;
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const {
    isCopied: isUrlCopied,
    copyToClipboard: copyUrl
  } = useCopyToClipboard();
  const {
    isCopied: isCurlCopied,
    copyToClipboard: copyCurl
  } = useCopyToClipboard();
  let curlCommand;
  let maskedUrl;
  let t3;
  let webhookUrl;
  if ($[0] !== inputSchema || $[1] !== secret || $[2] !== showSecret || $[3] !== workflowId) {
    const exampleBody = inputSchema ? deriveExampleBody(inputSchema) : undefined;
    webhookUrl = generatePerWorkflowWebhookUrl(workflowId, secret);
    curlCommand = generatePerWorkflowCurlCommand(workflowId, secret, exampleBody);
    maskedUrl = showSecret ? webhookUrl : webhookUrl.replace(secret, "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022");
    t3 = showSecret ? curlCommand : curlCommand.replace(secret, "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022");
    $[0] = inputSchema;
    $[1] = secret;
    $[2] = showSecret;
    $[3] = workflowId;
    $[4] = curlCommand;
    $[5] = maskedUrl;
    $[6] = t3;
    $[7] = webhookUrl;
  } else {
    curlCommand = $[4];
    maskedUrl = $[5];
    t3 = $[6];
    webhookUrl = $[7];
  }
  const maskedCurl = t3;
  const t4 = `border-ds-border-default bg-ds-surface-raised rounded-lg border p-4 ${className}`;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex items-center gap-2"><IconLink2Outline18 className="text-ds-text-muted h-4 w-4" /><h3 className="text-ds-text-primary text-sm font-semibold dark:text-zinc-100">Webhook URL</h3></div>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setIsExpanded(_temp);
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = isExpanded ? "Hide details" : "Show details";
  let t8;
  if ($[10] !== isExpanded || $[11] !== t7) {
    t8 = <div className="flex items-center justify-between gap-4">{t5}<Button variant="white" size="sm" type="button" onClick={t6} aria-expanded={isExpanded}>{t7}</Button></div>;
    $[10] = isExpanded;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== copyCurl || $[14] !== copyUrl || $[15] !== curlCommand || $[16] !== isCurlCopied || $[17] !== isExpanded || $[18] !== isRegenerating || $[19] !== isUrlCopied || $[20] !== maskedCurl || $[21] !== maskedUrl || $[22] !== onRegenerateSecret || $[23] !== showSecret || $[24] !== webhookUrl) {
    t9 = isExpanded && <div className="mt-4 space-y-4"><div className="space-y-2"><div className="flex items-center justify-between"><span className="label-xs text-ds-text-muted">URL</span><div className="flex items-center gap-2"><button type="button" onClick={() => setShowSecret(_temp2)} className="text-ds-text-muted hover:text-ds-text-secondary flex items-center gap-1 text-xs transition-colors dark:hover:text-zinc-300">{showSecret ? <IconEyeSlashOutline18 className="h-3 w-3" /> : <IconEyeOutline18 className="h-3 w-3" />}{showSecret ? "Hide" : "Show"} Secret</button>{onRegenerateSecret && <button type="button" onClick={onRegenerateSecret} disabled={isRegenerating} className="text-ds-text-muted hover:text-ds-text-secondary flex items-center gap-1 text-xs transition-colors disabled:opacity-50 dark:hover:text-zinc-300"><IconArrowRotateClockwiseOutline18 className={`h-3 w-3 ${isRegenerating ? "animate-spin" : ""}`} />Regenerate</button>}</div></div><div className="border-ds-border-default dark:bg-ds-neutral-800 group relative flex items-center gap-2 rounded-lg border bg-zinc-50 p-3"><code className="text-ds-text-primary flex-1 overflow-x-auto text-xs dark:text-zinc-100">{maskedUrl}</code><button type="button" onClick={() => copyUrl(webhookUrl)} className="text-ds-text-subtle hover:bg-ds-neutral-200 hover:text-ds-text-primary dark:text-ds-text-disabled dark:hover:bg-ds-neutral-700 flex-shrink-0 rounded p-1.5 transition-colors dark:hover:text-white" title="Copy URL to clipboard">{isUrlCopied ? <IconCheckOutline18 className="h-4 w-4 text-green-600 dark:text-green-400" /> : <IconCopyOutline18 className="h-4 w-4" />}</button></div></div><div className="space-y-2"><div className="flex items-center gap-2"><IconTerminalOutline18 className="text-ds-text-muted h-4 w-4" /><span className="label-xs text-ds-text-muted">cURL Command</span></div><div className="border-ds-border-default dark:bg-ds-neutral-800 group relative flex items-start gap-2 rounded-lg border bg-zinc-50 p-3"><pre className="text-ds-text-primary flex-1 overflow-x-auto whitespace-pre-wrap break-all text-xs dark:text-zinc-100">{maskedCurl}</pre><button type="button" onClick={() => copyCurl(curlCommand)} className="text-ds-text-subtle hover:bg-ds-neutral-200 hover:text-ds-text-primary dark:text-ds-text-disabled dark:hover:bg-ds-neutral-700 flex-shrink-0 rounded p-1.5 transition-colors dark:hover:text-white" title="Copy curl command to clipboard">{isCurlCopied ? <IconCheckOutline18 className="h-4 w-4 text-green-600 dark:text-green-400" /> : <IconCopyOutline18 className="h-4 w-4" />}</button></div></div><p className="label-xs">Customize the <code className="bg-ds-neutral-200 dark:bg-ds-neutral-700 rounded px-1">data</code> object with your fields. Access input data in your workflow with{" "}<code className="bg-ds-neutral-200 dark:bg-ds-neutral-700 rounded px-1">{"{{root.result.body.fieldName}}"}</code></p></div>;
    $[13] = copyCurl;
    $[14] = copyUrl;
    $[15] = curlCommand;
    $[16] = isCurlCopied;
    $[17] = isExpanded;
    $[18] = isRegenerating;
    $[19] = isUrlCopied;
    $[20] = maskedCurl;
    $[21] = maskedUrl;
    $[22] = onRegenerateSecret;
    $[23] = showSecret;
    $[24] = webhookUrl;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t4 || $[27] !== t8 || $[28] !== t9) {
    t10 = <div className={t4}>{t8}{t9}</div>;
    $[26] = t4;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}
function _temp2(prev_0) {
  return !prev_0;
}
function _temp(prev) {
  return !prev;
}